import { DataGlueGraph } from '@glueapp/graph-types';
import { validateRules } from './rules/validateRules';
import { ValidationError, ValidationRule } from './rules/ValidationRule';
import { CircularConnectionRule } from './rules/structural/CircularConnectionRule';
import { CircularFunctionReferenceRule } from './rules/structural/CircularFunctionReferenceRule';
import { IntegrationAccess } from '@glueapp/graph-types';
import { ConnectionIntegrityRule } from './rules/structural/ConnectionIntegrityRule';
import { TypeCompatibilityRule } from './rules/content/TypeCompatibilityRule';
import { FunctionInterfaceSynchronizationRule } from './rules/content/FunctionInterfaceSynchronizationRule';
import { IntegrationAvailabilityRule } from './rules/content/IntegrationAvailabilityRule';
import { ApiResponseContextRule } from './rules/content/ApiResponseContextRule';
import { ApiEndpointOverlapRule } from './rules/content/ApiEndpointOverlapRule';

export interface ConnectionValidation {
    id: number;
    valid: boolean;
    errorMessage: string | undefined;
}

export interface ValidationResult {
    connectionValidations: ConnectionValidation[];
    globalWarnings?: Array<{
        code: string;
        message: string;
        severity: 'warning';
        location: any;
    }>;
}

export function validateGraphStrucure(graph: DataGlueGraph): ValidationResult {
    const structuralRules = [CircularConnectionRule, CircularFunctionReferenceRule, ConnectionIntegrityRule];
    const result = validateRules(graph, structuralRules);
    return convertErrorsToResult(graph, result.errors);
}

export function validateGraphContent(graph: DataGlueGraph, integrationAccesses: IntegrationAccess[] = []): ValidationResult {
    const connectionRules: ValidationRule[] = [TypeCompatibilityRule];
    const connectionResult = validateRules(graph, connectionRules);

    const nodeRules: ValidationRule[] = [
        FunctionInterfaceSynchronizationRule,
        IntegrationAvailabilityRule,
        ApiResponseContextRule,
        ApiEndpointOverlapRule,
    ];
    const nodeResult = validateRules(graph, nodeRules);

    // Aggregate all errors
    const allErrors = [...connectionResult.errors, ...nodeResult.errors];

    return convertErrorsToResult(graph, allErrors);
}

function convertErrorsToResult(graph: DataGlueGraph, errors: ValidationError[]): ValidationResult {
    // Convert errors to connection validations
    const connectionValidations: ConnectionValidation[] = [];

    // Helper function to process connections and their errors
    const processConnections = (connections: any[], functionId?: string) => {
        for (const connection of connections) {
            let hasError = false;
            let errorMessage: string | undefined;

            // Check if any errors relate to this connection
            const relevantErrors = errors.filter(error => {
                // Check if error is for this connection ID
                if (
                    error.location?.connectionId !== connection.id &&
                    !(error.code === 'CIRCULAR_CONNECTION' && error.message.includes(connection.sendingNodeInstanceId.toString()))
                ) {
                    return false;
                }

                // Check if error is in the right context
                const errorFunctionId = error.location?.functionId;
                const errorContext = error.location?.context;

                if (!functionId) {
                    // Processing main graph - only include main graph errors
                    return errorContext === 'main graph' || (!errorFunctionId && !errorContext);
                } else {
                    // Processing function - only include function-specific errors
                    return errorFunctionId === functionId;
                }
            });

            if (relevantErrors.length > 0) {
                hasError = true;
                errorMessage = relevantErrors.map(e => e.message).join('; ');
            }

            connectionValidations.push({
                id: connection.id,
                valid: !hasError,
                errorMessage,
            });
        }
    };

    // Process root-level connections
    processConnections(graph.connections);

    // Process connections from all functions
    if (graph.functions) {
        for (const func of graph.functions) {
            processConnections(func.connections, func.id);
        }
    }

    // Convert warnings
    const globalWarnings = errors
        .filter(error => error.severity === 'warning')
        .map(error => ({
            code: error.code,
            message: error.message,
            severity: 'warning' as const,
            location: error.location || {},
        }));

    return {
        connectionValidations,
        globalWarnings: globalWarnings.length > 0 ? globalWarnings : undefined,
    };
}
